import { Classification } from "@/components/Classification";
import { LinkButton } from "@/components/LinkButton";
import { TeamName } from "@/components/TeamName";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { getProjectValue } from "@/utils/getProjectValue";
import { type OpportunitySearch } from "@repo/api";
import { Checkbox, Text, visibilityShowIcon } from "@repo/skapa";
import { T, getLanguageLocal, t } from "@repo/translation";
import {
  CoWorkerName,
  LocalizedDate,
  LocalizedDateAndTime,
  ProjectTitle,
  getCustomerName,
  translateOpportunityType,
  translateProjectStatus,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { type ChangeEvent } from "react";

import {
  ActionCell,
  CheckboxCell,
  ClickableRow,
  HideOnMobile,
  SecondRow,
} from "./ProjectsTable.styled";

interface ProjectsTableRowProps {
  project: OpportunitySearch;
  isSelected: boolean;
  onShowQuickview: (opportunityId: number) => void;
  onCheckboxChange: (
    e: ChangeEvent<HTMLInputElement>,
    project: OpportunitySearch
  ) => void;
}

export const ProjectsTableRow = ({
  project,
  isSelected,
  onShowQuickview,
  onCheckboxChange,
}: ProjectsTableRowProps) => {
  const { market, language, general, customerMode } = useSettings();

  return (
    <ClickableRow
      onClick={() => {
        onShowQuickview(project.opportunityId);
        trackAnalytics("show_project_quick_view");
      }}
    >
      <CheckboxCell>
        {!project.lockStatus && (
          <Checkbox
            id={`project-table-checkbox-${project.opportunityId}`}
            value={String(project.opportunityId)}
            subtle
            checked={isSelected}
            onChange={(e) => onCheckboxChange(e, project)}
            onClick={(e) => e.stopPropagation()}
            aria-label={t("action.select_project", {
              projectId: project.opportunityId,
            })}
          />
        )}
      </CheckboxCell>
      <HideOnMobile>
        <Classification
          id={project.opportunityId.toString()}
          classification={project.classification}
        />
      </HideOnMobile>
      <td>
        <Text textStyle="Heading.XS">
          {getCustomerName(
            {
              firstName: project.customerFirstName,
              lastName: project.customerLastName,
              organizationName: project.organizationName,
              customerType: project.customerType,
            },
            general?.localization?.reverse_names,
            customerMode
          )}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S">
            <ProjectTitle
              title={project.title}
              customerName={project.customerName}
              organizationName={project.organizationName}
            />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          <LocalizedDate date={project.createdAt} />
        </Text>
        <SecondRow>
          <Text textStyle="Body.S">
            <T translationKey="label.updated" />:{" "}
            <LocalizedDateAndTime date={project.updatedAt ?? ""} />
          </Text>
        </SecondRow>
      </td>
      <HideOnMobile>
        <Text textStyle="Heading.XS">
          {translateOpportunityType(project.opportunityType)}
        </Text>
      </HideOnMobile>
      <HideOnMobile>
        <Text textStyle="Heading.XS">
          {translateProjectStatus(project.status)}
        </Text>
      </HideOnMobile>
      <HideOnMobile>
        <Text textStyle="Heading.XS">
          {currencyFormatter({
            value: getProjectValue(project.displayAmount),
            currency: project.currency,
            locale: getLanguageLocal(language, market),
            showSign: true,
            disableDecimals: Boolean(general?.localization?.disable_decimals),
            fractions: 2,
          })}
        </Text>
      </HideOnMobile>
      <HideOnMobile>
        {project.assignedTo ? (
          <CoWorkerName email={project.assignedTo} textStyle="Heading.XS" />
        ) : project.assignedTeam ? (
          <TeamName teamId={project.assignedTeam} textStyle="Heading.XS" />
        ) : (
          <Text textStyle="Heading.XS">{t("label.unassigned")}</Text>
        )}
        <SecondRow>
          <Text textStyle="Body.S">{project.customerMeetingPointName}</Text>
        </SecondRow>
      </HideOnMobile>
      <ActionCell>
        <LinkButton
          to={`/opportunity/${project.opportunityId}`}
          text={t("action.manage_project", {
            projectId: project.opportunityId,
            projectTitle: project.title,
          })}
          ssrIcon={visibilityShowIcon}
          type="secondary"
          size="small"
          iconOnly
          onClick={() => {
            trackAnalytics("open_project_page");
          }}
        />
      </ActionCell>
    </ClickableRow>
  );
};
